import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Can TMS help if antidepressants haven't worked?",
    answer:
      "Yes. TMS is FDA-cleared specifically for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication. A pivotal randomized trial of 301 adults found active TMS superior to sham, and a real-world study of 307 patients reported about 58% response and 37% remission. TMS does not help everyone, and individual results vary.",
  },
  {
    question: "Do I need to stop my antidepressant before starting TMS?",
    answer:
      "No. Many patients continue their current antidepressant during TMS. TMS is a non-medication treatment that can be used alongside medication. Do not stop or change any medication without consulting your prescriber, as abrupt discontinuation can cause withdrawal symptoms and relapse.",
  },
  {
    question: "How effective is TMS after antidepressant failure?",
    answer:
      "In a real-world observational study of 307 patients who had not improved on antidepressants, approximately 58% met response criteria and 37% met remission criteria after completing a TMS course. The study had no control group, and roughly 40% or more of patients do not meet response criteria. Your individual likelihood depends on your history.",
  },
];

export const pageCanTmsHelpAfterAntidepressants: LearnPageData = {
  slug: "can-tms-help-after-antidepressants-fail",
  title: "Can TMS Help After Antidepressants Fail? | Neuro Wellness",
  description:
    "TMS is FDA-cleared for adults whose depression has not improved on antidepressants. Learn how TMS works after medication failure and what the research shows.",
  canonical: "/learn/can-tms-help-after-antidepressants-fail",
  h1: "Can TMS Help After Antidepressants Fail?",
  breadcrumbLabel: "Can TMS Help After Antidepressants Fail?",
  crumbs: baseCrumbs("Can TMS Help After Antidepressants Fail?"),
  shortAnswer:
    "Yes. TMS is FDA-cleared specifically for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication. A pivotal randomized trial of 301 adults found active TMS superior to sham. A real-world study of 307 patients reported about 58% response and 37% remission. TMS does not help everyone, and individual results vary.",
  keyTakeaways: [
    "TMS is FDA-cleared for adults whose depression has not improved on antidepressants.",
    "A randomized trial of 301 adults found active TMS superior to sham.",
    "A real-world study reported 58% response and 37% remission.",
    "TMS is non-invasive, medication-free, and does not require anesthesia.",
    "About 40% or more of patients do not meet response criteria.",
  ],

  sections: [
    {
      heading: "How TMS Works After Medication Failure",
      body: (
        <>
          {P(
            "TMS uses magnetic pulses to stimulate the left dorsolateral prefrontal cortex, a brain region involved in mood regulation. Unlike medication, which affects the entire body through the bloodstream, TMS targets a specific brain area. It is FDA-cleared for adults with major depressive disorder who have not improved adequately on antidepressant medication in the current episode.",
          )}
        </>
      ),
    },
    {
      heading: "What the Research Shows",
      body: (
        <>
          {P(
            "A 301-person randomized sham-controlled trial (O'Reardon et al., 2007) found active TMS superior to sham over 4 to 6 weeks, supporting the first FDA clearance. A 307-patient real-world study (Carpenter et al., 2012) reported approximately 58% response and 37% remission on clinician-rated scales. The real-world study had no control group, and results vary between individuals.",
          )}
        </>
      ),
    },
    {
      heading: "TMS vs. SAINT® After Medication Failure",
      body: (
        <>
          {P(
            "Conventional TMS takes about 6 weeks of daily weekday sessions. SAINT® is an accelerated, fMRI-guided form of TMS that delivers 50 sessions in 5 consecutive days. Both are FDA-cleared for adults whose depression has not improved on antidepressants. A prior course of standard TMS does not rule out SAINT®.",
          )}
          {P(
            <>
              <a
                href="/learn/saint-vs-traditional-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Compare SAINT® vs. traditional TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "O'Reardon JP, et al. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17196600/",
    },
    {
      citation: "Carpenter LL, et al. Depress Anxiety. 2012;29(7):587-596.",
      url: "https://pubmed.ncbi.nlm.nih.gov/22713944/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/can-tms-help-after-antidepressants-fail#webpage`,
        name: "Can TMS Help After Antidepressants Fail?",
        url: `${SITE_URL}/learn/can-tms-help-after-antidepressants-fail`,
        description: "How TMS works after medication failure.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/can-tms-help-after-antidepressants-fail#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Can TMS Help After Antidepressants Fail?")),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "SAINT® TMS",
      href: "/saint",
      description: "5-day accelerated TMS",
    },
    {
      title: "What Is TMS Therapy?",
      href: "/learn/what-is-tms-therapy-for-depression",
      description: "How TMS works",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
